import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Col, Drawer, Empty, Form, Grid, Input, Modal, Popconfirm,
  Row, Select, Space, Spin, Statistic, Tag, Tree, Typography, message
} from 'antd';
import {
  ApartmentOutlined, CompressOutlined, DeleteOutlined, EditOutlined,
  ExpandOutlined, FolderOpenOutlined, PlusOutlined, ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import './ChartOfAccountsPage.css';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const TYPE_META = {
  asset: { label: 'สินทรัพย์', color: 'blue' },
  liability: { label: 'หนี้สิน', color: 'red' },
  equity: { label: 'ส่วนของเจ้าของ', color: 'purple' },
  income: { label: 'รายได้', color: 'green' },
  expense: { label: 'ค่าใช้จ่าย', color: 'orange' }
};

const TYPE_ORDER = Object.keys(TYPE_META);
const normalizeAccount = account => ({
  ...account,
  id: Number(account.id),
  code: String(account.code ?? account.CODE ?? ''),
  name: String(account.name ?? account.NAME ?? ''),
  parent_id: account.parent_id ? Number(account.parent_id) : null,
  allow_posting: account.allow_posting === undefined ? true : Boolean(Number(account.allow_posting)),
  is_active: account.is_active === undefined ? true : Boolean(Number(account.is_active))
});

const buildAccountTree = accounts => {
  const byId = new Map(accounts.map(account => [account.id, { ...account, key: `account-${account.id}`, children: [] }]));
  const rootsByType = Object.fromEntries(TYPE_ORDER.map(type => [type, []]));

  const hasCircularParent = account => {
    const visited = new Set([account.id]);
    let parentId = account.parent_id;
    while (parentId) {
      if (visited.has(parentId)) return true;
      visited.add(parentId);
      parentId = byId.get(parentId)?.parent_id;
    }
    return false;
  };

  accounts.forEach(account => {
    const node = byId.get(account.id);
    const parent = account.parent_id ? byId.get(account.parent_id) : null;
    if (parent && parent.type === account.type && !hasCircularParent(account)) parent.children.push(node);
    else (rootsByType[account.type] || (rootsByType[account.type] = [])).push(node);
  });

  const sortNodes = nodes => nodes.sort((a, b) => a.code.localeCompare(b.code, 'th', { numeric: true })).map(node => ({
    ...node,
    children: sortNodes(node.children)
  }));

  return TYPE_ORDER.map(type => ({
    key: `type-${type}`,
    type,
    isGroup: true,
    selectable: false,
    count: accounts.filter(account => account.type === type).length,
    children: sortNodes(rootsByType[type] || [])
  })).filter(group => group.children.length);
};

const filterTree = (nodes, keyword) => {
  const query = keyword.trim().toLowerCase();
  if (!query) return nodes;
  return nodes.map(node => {
    const ownMatch = !node.isGroup && `${node.code} ${node.name}`.toLowerCase().includes(query);
    const children = ownMatch ? node.children : filterTree(node.children || [], query);
    if (node.isGroup || ownMatch || children.length) return { ...node, children };
    return null;
  }).filter(node => node && (!node.isGroup || node.children.length));
};

const collectExpandableKeys = nodes => nodes.flatMap(node => [
  ...((node.children || []).length ? [node.key] : []),
  ...collectExpandableKeys(node.children || [])
]);

const ChartOfAccountsPage = () => {
  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [form] = Form.useForm();
  const selectedType = Form.useWatch('type', form);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/accounting/accounts');
      const next = (res.data?.data || []).map(normalizeAccount);
      setAccounts(next);
      setSelectedAccount(current => current ? next.find(item => item.id === current.id) || null : null);
    } catch (error) {
      message.error(error.response?.data?.message || 'ไม่สามารถดึงข้อมูลผังบัญชีได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccounts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fullTree = useMemo(() => buildAccountTree(accounts), [accounts]);
  const visibleTree = useMemo(() => filterTree(fullTree, search), [fullTree, search]);
  const allExpandableKeys = useMemo(() => collectExpandableKeys(visibleTree), [visibleTree]);

  useEffect(() => { setExpandedKeys(allExpandableKeys); }, [search, accounts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const descendantIds = useMemo(() => {
    if (!editingItem) return new Set();
    const result = new Set([editingItem.id]);
    let changed = true;
    while (changed) {
      changed = false;
      accounts.forEach(account => {
        if (account.parent_id && result.has(account.parent_id) && !result.has(account.id)) {
          result.add(account.id);
          changed = true;
        }
      });
    }
    return result;
  }, [accounts, editingItem]);

  const parentOptions = accounts
    .filter(account => account.type === selectedType && !descendantIds.has(account.id))
    .map(account => ({ value: account.id, label: `${account.code} — ${account.name}` }));

  const openCreate = parent => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue(parent ? { type: parent.type, parent_id: parent.id } : { parent_id: null });
    setIsModalVisible(true);
  };

  const openEdit = account => {
    setEditingItem(account);
    form.setFieldsValue({ code: account.code, name: account.name, type: account.type, parent_id: account.parent_id || undefined });
    setIsModalVisible(true);
  };

  const handleSubmit = async values => {
    try {
      if (editingItem) {
        await api.put(`/api/accounting/accounts/${editingItem.id}`, values);
        message.success('แก้ไขบัญชีเรียบร้อย');
      } else {
        await api.post('/api/accounting/accounts', values);
        message.success('เพิ่มบัญชีเรียบร้อย');
      }
      setIsModalVisible(false);
      form.resetFields();
      setEditingItem(null);
      await fetchAccounts();
    } catch (error) {
      message.error(error.response?.data?.message || 'ทำรายการไม่สำเร็จ');
    }
  };

  const handleDelete = async id => {
    try {
      await api.delete(`/api/accounting/accounts/${id}`);
      message.success('ลบรายการเรียบร้อย');
      if (selectedAccount?.id === id) setSelectedAccount(null);
      await fetchAccounts();
    } catch (error) {
      message.error(error.response?.data?.message || 'ลบรายการไม่สำเร็จ');
    }
  };

  const selectAccount = (_, info) => {
    if (info.node.isGroup) return;
    const account = accounts.find(item => item.id === info.node.id);
    setSelectedAccount(account || null);
    if (isMobile && account) setDetailVisible(true);
  };

  const titleRender = node => {
    if (node.isGroup) {
      const meta = TYPE_META[node.type];
      return <div className="coa-group-title"><span><FolderOpenOutlined /> {meta.label}</span><Tag color={meta.color}>{node.count}</Tag></div>;
    }
    return (
      <div className="coa-tree-node-title">
        <span className="coa-account-code">{node.code}</span>
        <span className="coa-account-name">{node.name}</span>
        {!node.is_active && <Tag>ปิดใช้งาน</Tag>}
        {node.allow_posting === false && <Tag color="gold">บัญชีคุม</Tag>}
      </div>
    );
  };

  const accountDetails = selectedAccount ? (
    <div className="coa-detail-panel">
      <div className="coa-detail-heading">
        <div><Text type="secondary">รหัสบัญชี</Text><Title level={3}>{selectedAccount.code}</Title></div>
        <Tag color={TYPE_META[selectedAccount.type]?.color}>{TYPE_META[selectedAccount.type]?.label}</Tag>
      </div>
      <Title level={4} className="coa-detail-name">{selectedAccount.name}</Title>
      <div className="coa-detail-grid">
        <div><span>บัญชีแม่</span><strong>{accounts.find(item => item.id === selectedAccount.parent_id)?.name || 'ระดับหลัก'}</strong></div>
        <div><span>ยอดปกติ</span><strong>{selectedAccount.normal_balance === 'credit' ? 'เครดิต' : 'เดบิต'}</strong></div>
        <div><span>การลงรายการ</span><strong>{selectedAccount.allow_posting ? 'ลงรายการได้' : 'บัญชีคุม'}</strong></div>
        <div><span>สถานะ</span><strong>{selectedAccount.is_active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}</strong></div>
      </div>
      <Space wrap className="coa-detail-actions">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(selectedAccount)}>เพิ่มบัญชีย่อย</Button>
        <Button icon={<EditOutlined />} onClick={() => openEdit(selectedAccount)}>แก้ไข</Button>
        <Popconfirm title="ยืนยันการลบบัญชีนี้?" description="บัญชีที่ถูกใช้งานในสมุดรายวันอาจไม่สามารถลบได้" onConfirm={() => handleDelete(selectedAccount.id)} okText="ลบ" cancelText="ยกเลิก">
          <Button danger icon={<DeleteOutlined />}>ลบ</Button>
        </Popconfirm>
      </Space>
    </div>
  ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="เลือกบัญชีเพื่อดูรายละเอียด" />;

  return (
    <div className="chart-of-accounts-page">
      <div className="coa-page-heading">
        <div><Title level={2}><ApartmentOutlined /> ผังบัญชี</Title><Text type="secondary">จัดการโครงสร้างบัญชีหลักและบัญชีย่อย</Text></div>
        <Space><Button icon={<ReloadOutlined />} onClick={fetchAccounts}>รีเฟรช</Button><Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(null)}>เพิ่มบัญชี</Button></Space>
      </div>

      <Row gutter={[12,12]} className="coa-summary-row">
        <Col xs={8}><Card size="small"><Statistic title="บัญชีทั้งหมด" value={accounts.length} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="ลงรายการได้" value={accounts.filter(item => item.allow_posting).length} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="บัญชีคุม" value={accounts.filter(item => !item.allow_posting).length} /></Card></Col>
      </Row>

      <Card className="coa-main-card">
        <div className="coa-toolbar">
          <Input allowClear value={search} onChange={event => setSearch(event.target.value)} prefix={<SearchOutlined />} placeholder="ค้นหารหัสหรือชื่อบัญชี" />
          <Space.Compact>
            <Button icon={<ExpandOutlined />} onClick={() => setExpandedKeys(allExpandableKeys)}>{isMobile ? '' : 'ขยายทั้งหมด'}</Button>
            <Button icon={<CompressOutlined />} onClick={() => setExpandedKeys([])}>{isMobile ? '' : 'ย่อทั้งหมด'}</Button>
          </Space.Compact>
        </div>

        <Row gutter={[18,18]}>
          <Col xs={24} lg={14}>
            <Spin spinning={loading}>
              {visibleTree.length ? <Tree className="coa-tree" blockNode showLine treeData={visibleTree} titleRender={titleRender} expandedKeys={expandedKeys} onExpand={setExpandedKeys} selectedKeys={selectedAccount ? [`account-${selectedAccount.id}`] : []} onSelect={selectAccount} /> : <Empty description="ไม่พบบัญชีที่ค้นหา" />}
            </Spin>
          </Col>
          {!isMobile && <Col lg={10}><Card className="coa-detail-card" size="small">{accountDetails}</Card></Col>}
        </Row>
      </Card>

      <Drawer className="coa-mobile-detail-drawer" title="รายละเอียดบัญชี" placement="bottom" height="auto" open={detailVisible} onClose={() => setDetailVisible(false)}>{accountDetails}</Drawer>

      <Modal title={editingItem ? 'แก้ไขผังบัญชี' : 'เพิ่มผังบัญชีใหม่'} open={isModalVisible} onCancel={() => { setIsModalVisible(false); setEditingItem(null); }} onOk={() => form.submit()} okText="บันทึก" cancelText="ยกเลิก" width={isMobile ? 'calc(100vw - 20px)' : 560} className="coa-form-modal">
        <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={changed => { if (changed.type) form.setFieldValue('parent_id', undefined); }}>
          <Form.Item name="code" label="รหัสบัญชี" rules={[{ required: true, message: 'กรุณาระบุรหัสบัญชี' }]}><Input placeholder="เช่น 110100" /></Form.Item>
          <Form.Item name="name" label="ชื่อบัญชี" rules={[{ required: true, message: 'กรุณาระบุชื่อบัญชี' }]}><Input placeholder="เช่น เงินฝากธนาคาร" /></Form.Item>
          <Form.Item name="type" label="ประเภทบัญชี" rules={[{ required: true, message: 'กรุณาเลือกประเภทบัญชี' }]}><Select placeholder="เลือกประเภท" options={TYPE_ORDER.map(value => ({ value, label: TYPE_META[value].label }))} /></Form.Item>
          <Form.Item name="parent_id" label="บัญชีแม่" extra="เว้นว่างหากต้องการให้อยู่ระดับหลัก"><Select allowClear showSearch optionFilterProp="label" disabled={!selectedType} placeholder="เลือกลำดับบัญชีแม่" options={parentOptions} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ChartOfAccountsPage;
